import { Navigate } from "react-router-dom";
import { useBusiness } from "@/core/business";

interface HiddenWhenSpiffOnlyProps {
  children: React.ReactNode;
  fallbackPath?: string;
}

/**
 * Wrapper component that hides content when the business is spiff-only.
 * If the business is spiff-only, it will navigate to the fallback path.
 * Otherwise, it renders the children normally.
 *
 * Includes loading state check to prevent content flash during data fetch.
 *
 * @param children - The content to show when NOT spiff-only
 * @param fallbackPath - The path to redirect to when spiff-only (defaults to "/spiffs")
 *
 * @example
 * ```tsx
 * <HiddenWhenSpiffOnly fallbackPath="/spiffs">
 *   <Dashboard />
 * </HiddenWhenSpiffOnly>
 * ```
 */
export const HiddenWhenSpiffOnly: React.FC<HiddenWhenSpiffOnlyProps> = ({
  children,
  fallbackPath = "/spiffs",
}) => {
  const { isSpiffOnly, isLoading } = useBusiness();

  if (isLoading) {
    return null;
  }

  if (isSpiffOnly) {
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
};
